import { Injectable, OnDestroy, Inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EncryptionService } from './encyption.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { io } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { AuthGuardService } from './auth-guard.service';
import { DOCUMENT } from '@angular/common';
import { ConfigService } from './config.service';
@Injectable({
    providedIn: 'root',
})
export class ChatService implements OnDestroy {
    private socket: any;
    private pendingUsername: string | null = null;
    private sessionReady = false;
    private pendingMessages: Array<{ message: any; cb: any }> = [];
    protected openchat: BehaviorSubject<boolean>;
    protected updateusername$: BehaviorSubject<any>;
    constructor(
        private http: HttpClient,
        private authGuardService: AuthGuardService,
        private encryption: EncryptionService,
        protected configService: ConfigService,
        @Inject(DOCUMENT) protected document: any
    ) {
        this.openchat = new BehaviorSubject(false);
        this.updateusername$ = new BehaviorSubject(null);
    }

    connectToServer() {
        try {
            if (!this.socket) {
                let url = 'https://' + this.document.domain;
                if (!environment.production) {
                    url = 'http://127.0.0.1:' + this.configService.getConfig().ports.socket;
                }
                const namespace = this.authGuardService.getPartnerId()?.replace(/-/g, '_');
                const usrnm = window.localStorage.getItem(this.authGuardService.getPartnerId() + '_sessionid');
                const prtid = this.authGuardService.getPartnerId();
                this.socket = io(url + '/' + namespace, {
                    transports: ['websocket'],
                    reconnection: true,
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 500,
                    reconnectionDelayMax: 3000,
                    query: {
                        web: true,
                        username: usrnm,
                        partnerid: prtid,
                        namespace: namespace
                    }
                });
                this.socket.on('connect', () => {
                    // The transport is connected, but the server still needs
                    // to restore the chat user and its Socket.IO room.
                    this.sessionReady = false;
                });

                this.socket.io.on('reconnect_attempt', () => {
                    this.syncSocketSessionQuery();
                });

                this.socket.on('disconnect', (reason: string) => {
                    this.sessionReady = false;
                    this.syncSocketSessionQuery();
                    // Socket.IO reconnects transport failures by itself. An
                    // explicit server disconnect needs one manual reconnect.
                    if (reason === 'io server disconnect') {
                        this.socket.connect();
                    }
                });

                this.socket.on('connect_error', () => {
                    console.log('chat connection unavailable; retrying');
                });

                this.socket.on('kill', () => {
                    this.sessionReady = false;
                    this.pendingMessages = [];
                    this.socket.removeAllListeners();
                    this.socket.disconnect();
                });

                this.checkusername();
            }
        } catch (error) {
            console.log(error);
        }
    }

    closeconnection() {
        this.socket.removeAllListeners();
        this.socket = null;
    }

    checkusername() {
        this.socket.on('updatename', (data) => {
            if (
                this.pendingUsername &&
                data?.username !== this.pendingUsername
            ) {
                return;
            }
            window.localStorage.setItem(this.authGuardService.getPartnerId() + '_sessionid', data.username);
            this.sessionReady = true;
            this.updateusername$.next(data);
            this.flushPendingMessages();
        });
    }

    private syncSocketSessionQuery() {
        if (!this.socket) return;
        const partnerid = this.authGuardService.getPartnerId();
        const username = window.localStorage.getItem(partnerid + '_sessionid');
        const namespace = partnerid?.replace(/-/g, '_');
        const query = {
            web: true,
            username,
            partnerid,
            namespace,
        };
        this.socket.io.opts.query = query;
        this.socket.auth = query;
    }

    private flushPendingMessages() {
        if (!this.socket?.connected || !this.sessionReady || !this.pendingMessages.length) {
            return;
        }
        const messages = this.pendingMessages.splice(0);
        for (const { message, cb } of messages) {
            this.socket.emit('sendMessage', message, cb);
        }
    }

    
    ngOnDestroy(): void {
        this.socket && this.socket.disconnect();
        this.socket = null;
    }

    get openchat$(): Observable<any> {
        return this.openchat.asObservable()
    }

    setOpenChat() {
        this.openchat.next(true);
    }

    // EMITTER
    sendMessage<T>(message: T, cb) {
        try {
            if (message) {
                if (!this.socket?.connected || !this.sessionReady) {
                    this.pendingMessages.push({ message, cb });
                    return;
                }
                this.socket.emit('sendMessage', message, cb);
            } else {
                this.socket.removeAllListeners();
            }
        } catch (error) {
            this.socket.removeAllListeners();
        }
    }

    // get my messages
    getmymesages<T, A>(username: T, cb: (data: A) => void) {
        try {
            if (username) {
                this.socket.emit('getmymesages', { username: username }, cb);
            } else {
                this.socket.removeAllListeners();
            }
        } catch (error) {
            this.socket.removeAllListeners();
        }
    }

    // HANDLER
    onNewMessage<T>() {
        return new Observable(observer => {
            this.socket.on('msgfrombck', (message: T) => {
                observer.next(message);
            });
        });
    }

    onAiProgress<T>(): Observable<T> {
        return new Observable<T>(observer => {
            this.socket.on('aiProgress', (progress: T) => {
                observer.next(progress);
            });
        });
    }

    // HANDLER typing
    onTyping() {
        return new Observable(observer => {
            this.socket.on('sendTyping', (typing) => {
                observer.next(typing);
            });
        });
    }

    // HANDLER screen
    checkScreen() {
        return new Observable(observer => {
            this.socket.on('sendsmm', (typing) => {
                observer.next(typing);
            });
        });
    }

    // HANDLER send screen
    sendScreen(user, data) {
        try {
            if (user) {
                this.socket.emit('getsmm', { user: user, data: data });
            } else {
                this.socket.removeAllListeners();
            }
        } catch (error) {
            this.socket.removeAllListeners();
        }
    }

    imgPermission() {
        return new Observable(observer => {
            this.socket?.on('imgPermission', (prm) => {
                observer.next(prm);
            });
        });
    }

    // HANDLER typing
    onsendStopRobot() {
        return new Observable(observer => {
            this.socket.on('sendStopRobot', (data) => {
                observer.next(data);
            });
        });
    }

    // Send Typing
    sendTyping(user, typing) {
        try {
            if (user) {
                this.socket.emit('sendTyping', { user: user, typing: typing });
            } else {
                this.socket.removeAllListeners();
            }
        } catch (error) {
            this.socket.removeAllListeners();
        }
    }

    // HANDLER
    updateusername() {
        return this.updateusername$.asObservable();
    }

    resetUsername(oldname, newname = ''): Promise<boolean> {
        this.pendingUsername = newname || null;

        return new Promise((resolve) => {
            if (!this.socket?.connected) {
                this.pendingUsername = null;
                resolve(false);
                return;
            }

            let settled = false;
            const finish = (success: boolean) => {
                if (settled) return;
                settled = true;
                clearTimeout(timeout);
                if (!success) this.pendingUsername = null;
                resolve(success);
            };
            const timeout = setTimeout(() => finish(false), 5000);

            this.socket.emit(
                'resetwebusername',
                { newname: newname, oldname: oldname },
                (result) => {
                    const success = result?.ok === true && result?.username === newname;
                    finish(success);
                }
            );
        });
    }

    sendemail(data, cb) {
        this.socket.emit('sendEmail', data, cb);
    }

    request(obj: Object, suffix: string): Observable<any> {
        obj = this.encryption.encrypt(obj);
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'ngsw-bypass': ''
            })
        };
        return this.http.post('api/' + suffix, { body: obj }, httpOptions)
            .pipe(
                distinctUntilChanged()
            );
    }
}
